import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
    BUSINESSES_PATH,
    SETTINGS_PATH,
    PERSONS_PATH,
    SERVICES_PATH,
    PRODUCTS_PATH,
    TARIFFS_PATH,
    HISTORY_PATH,
    METRICS_PATH,
    PAYMENTS_INCOME_PATH,
    PAYMENTS_OUTCOME_PATH,
    AGENDA_PATH,
    SERVICE_ORDERS_PATH,
    BALANCE_PATH,
    ISSUES_PATH,
    SUBSCRIPTIONS_PATH,
    EMAIL_CAMPAIGNS_PATH,
    EMAIL_TEMPLATES_PATH,
    RESERVATIONS_EXPORT_PATH,
    INVENTORY_ITEMS_PATH,
    OPERATIONS_PATH,
    FLIGHTS_PATH,
    AFIP_HISTORY_PATH,
    ASSIGNMENTS_PATH,
} from '@/lib/navigation';
import {
    IconUsersGroup,
    IconAirBalloon,
    IconBuildingStore,
    IconGraph,
    IconHistory,
    IconScale,
    IconPackages,
    IconMailFast,
    IconTemplate,
    IconRoute,
    IconPin,
    IconSettings,
    IconX,
    IconCalendarWeek,
    IconClipboardCheck,
    IconEyeglass2,
    IconPlaneInflight,
    IconReceiptTax,
    IconRepeat,
    IconCashPlus,
    IconCashMinus,
    IconCalendarClock,
    IconAlertTriangle,
    IconDiscount,
} from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { useMenuState } from '@/hooks/use-menu-state';
import UserMenu from './user-menu';
import { ActionIcon } from '@mantine/core';
import { localUser } from '@/services/session-service';

export interface MenuItem {
    id?: number;
    permissionKey?: string;
    type?: 'menu-link' | 'section' | 'divider';
    path?: string;
    label?: string;
    description?: string;
    icon?: React.ReactNode;
    divider?: boolean;
    badge?: string | number;
    section?: string;
    highlight?: boolean;
}

export const MENU_PERMISSIONS_LIST: { key: string; labelKey: string }[] = [
    { key: 'reservations', labelKey: 'reservations' },
    { key: 'orders', labelKey: 'orders' },
    { key: 'operations', labelKey: 'operations' },
    { key: 'flights', labelKey: 'flights' },
    { key: 'assignments', labelKey: 'assignments' },
    { key: 'balance', labelKey: 'balance' },
    { key: 'payments_income', labelKey: 'payments_income' },
    { key: 'payments_outcome', labelKey: 'payments_outcome' },
    { key: 'agenda', labelKey: 'agenda' },
    { key: 'subscriptions', labelKey: 'subscriptions' },
    { key: 'afip_vouchers', labelKey: 'afip_vouchers' },
    { key: 'tariffs', labelKey: 'tariffs' },
    { key: 'services', labelKey: 'services' },
    { key: 'products', labelKey: 'products' },
    { key: 'businesses', labelKey: 'businesses' },
    { key: 'persons', labelKey: 'persons' },
    { key: 'inventory', labelKey: 'inventory' },
    { key: 'campaigns', labelKey: 'campaigns' },
    { key: 'templates', labelKey: 'templates' },
    { key: 'tickets', labelKey: 'tasks' },
    { key: 'issues', labelKey: 'issues' },
    { key: 'settings', labelKey: 'settings' },
    { key: 'metrics', labelKey: 'metrics' },
    { key: 'history', labelKey: 'history' },
];

export const getMenuItems = (t: TFunction): MenuItem[] => {
    const iconSize = 18;
    const iconStroke = 1.5;
    const iconColor = undefined; // uses the text color

    return [
        {
            id: 420,
            permissionKey: 'reservations',
            type: 'menu-link' as const,
            path: RESERVATIONS_EXPORT_PATH,
            label: t('reservations'),
            icon: <IconCalendarWeek size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            id: 1013,
            permissionKey: 'orders',
            type: 'menu-link' as const,
            path: SERVICE_ORDERS_PATH,
            label: t('orders'),
            icon: <IconRoute size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            id: 1016,
            permissionKey: 'operations',
            type: 'menu-link' as const,
            path: OPERATIONS_PATH,
            label: t('operations'),
            icon: <IconEyeglass2 size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            id: 1017,
            permissionKey: 'flights',
            type: 'menu-link' as const,
            path: FLIGHTS_PATH,
            label: t('flights'),
            icon: <IconPlaneInflight size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            divider: true
        },
        {
            id: 1018,
            permissionKey: 'assignments',
            type: 'menu-link' as const,
            path: ASSIGNMENTS_PATH,
            label: t('assignments'),
            icon: <IconClipboardCheck size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            divider: true
        },
        {
            id: 78,
            permissionKey: 'balance',
            label: t('balance'),
            path: BALANCE_PATH,
            icon: <IconScale size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            permissionKey: 'payments_income',
            label: t('payments_income'),
            path: PAYMENTS_INCOME_PATH,
            icon: <IconCashPlus size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            permissionKey: 'payments_outcome',
            label: t('payments_outcome'),
            path: PAYMENTS_OUTCOME_PATH,
            icon: <IconCashMinus size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true
        },
        {
            id: 1055,
            permissionKey: 'agenda',
            label: t('agenda'),
            path: AGENDA_PATH,
            icon: <IconCalendarClock size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true
        },
        {
            id: 1049,
            permissionKey: 'AFIP',
            label: t('AFIP'),
            path: AFIP_HISTORY_PATH,
            icon: <IconReceiptTax size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 79,
            permissionKey: 'subscriptions',
            label: t('subscriptions'),
            path: SUBSCRIPTIONS_PATH,
            icon: <IconRepeat size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true,
        },
        {
            id: 120,
            permissionKey: 'tariffs',
            type: 'menu-link' as const,
            path: TARIFFS_PATH,
            label: t('tariffs'),
            icon: <IconDiscount size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true,
        },
        {
            id: 121,
            permissionKey: 'services',
            type: 'menu-link' as const,
            path: SERVICES_PATH,
            label: t('services'),
            icon: <IconAirBalloon size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 122,
            permissionKey: 'services',
            type: 'menu-link' as const,
            path: PRODUCTS_PATH,
            label: t('products'),
            icon: <IconPackages size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 3,
            permissionKey: 'businesses',
            type: 'menu-link' as const,
            path: BUSINESSES_PATH,
            label: t('businesses'),
            icon: <IconBuildingStore size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 2,
            permissionKey: 'persons',
            type: 'menu-link' as const,
            path: PERSONS_PATH,
            label: t('persons'),
            icon: <IconUsersGroup size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 9,
            permissionKey: 'inventory',
            type: 'menu-link' as const,
            path: INVENTORY_ITEMS_PATH,
            label: t('inventory'),
            icon: <IconPackages size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true,
        },
        {
            id: 162,
            permissionKey: 'campaigns',
            type: 'menu-link' as const,
            path: EMAIL_CAMPAIGNS_PATH,
            label: t('campaigns'),
            icon: <IconMailFast size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 163,
            permissionKey: 'templates',
            type: 'menu-link' as const,
            path: EMAIL_TEMPLATES_PATH,
            label: t('templates'),
            icon: <IconTemplate size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true,
        },
        // {
        //     id: 16,
        //     permissionKey: 'tickets',
        //     type: 'menu-link' as const,
        //     path: TICKETS_PATH,
        //     label: t('tasks'),
        //     icon: <IconListDetails size={iconSize} stroke={iconStroke} color={iconColor} />
        // },
        {
            id: 800,
            permissionKey: 'issues',
            label: t('issues'),
            path: ISSUES_PATH,
            icon: <IconAlertTriangle size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            divider: true,
        },
        {
            id: 13,
            permissionKey: 'settings',
            type: 'menu-link' as const,
            path: SETTINGS_PATH,
            label: t('settings'),
            icon: <IconSettings size={iconSize} stroke={iconStroke} color={iconColor} />,
        },
        {
            id: 77,
            permissionKey: 'metrics',
            type: 'menu-link' as const,
            path: METRICS_PATH,
            label: t('metrics'),
            icon: <IconGraph size={iconSize} stroke={iconStroke} color={iconColor} />
        },
        {
            id: 14,
            permissionKey: 'history',
            type: 'menu-link' as const,
            path: HISTORY_PATH,
            label: t('history'),
            icon: <IconHistory size={iconSize} stroke={iconStroke} color={iconColor} />
        },
    ];
}


export default function LeftMenu(): JSX.Element {
    const { t } = useTranslation();

    const location = useLocation();
    const { isOpen, isMobile, closeMobileMenu, isPinned, togglePinned } = useMenuState();
    const [isHovered, setIsHovered] = React.useState(false);

    const isCollapsed = !isMobile && !isPinned && !isHovered;
    const currentWidth = isMobile ? '256px' : (isCollapsed ? '70px' : '250px');

    const user = localUser();
    const menuItems = getMenuItems(t);

    const visibleMenuItems = menuItems
        .filter((item) => {
            if (item.permissionKey && user?.permissions && user.permissions[item.permissionKey] === false) {
                return false;
            }
            return true;
        })
        .filter((item, idx, arr) => {
            if (item.divider || item.type === 'divider') {
                const prev = arr[idx - 1];
                const next = arr[idx + 1];
                if (!prev || prev.divider || prev.type === 'divider') return false;
                if (!next || next.divider || next.type === 'divider') return false;
            }
            return true;
        });

    const isActivePath = (path: string) => {
        const basePath = path.split('/:')[0];

        if (path.includes('?')) {
            const [pathPart, queryPart] = path.split('?');
            const currentPath = location.pathname + location.search;
            return currentPath === path ||
                (location.pathname === pathPart && location.search.includes(queryPart));
        }

        return location.pathname === path ||
            location.pathname.startsWith(basePath + '/');
    };

    const renderMenuItem = (item: MenuItem, index: number) => {
        if (item.section || item.divider || item.type === 'divider') {
            return (
                <div key={item.section || index} className={`border-t border-black/[0.04] mx-1 ${isMobile ? 'my-4' : 'my-3'}`} />
            );
        }

        const isActive = isActivePath(item.path || '');

        const menuItemContent = (
            <div
                className={`
                    flex items-center
                    justify-start
                    rounded-xl
                    transition-all duration-150
                    whitespace-nowrap
                    w-full
                    hover:bg-gray-100 hover:text-gray-900
                    ${isMobile ? 'h-14 px-4 text-base font-bold' : 'h-8 px-1.5 text-[13px] font-medium'}
                    ${isActive
                        ? 'text-gray-900 bg-gray-100/90 shadow-sm border border-gray-200/20'
                        : 'text-gray-700 bg-transparent'
                    }
                `}
            >
                <div className={`
                    flex items-center justify-center flex-shrink-0 transition-colors duration-150 
                    ${isMobile ? 'w-10 h-10 [&>svg]:w-6 [&>svg]:h-6' : 'w-8 h-8'}
                    ${isActive
                        ? 'text-green-600'
                        : 'text-gray-500 group-hover:text-gray-900'}
                `}>
                    {item.icon}
                </div>
                <div className={`
                    overflow-hidden transition-all duration-300 ease-in-out flex-1
                    ${isCollapsed ? 'max-w-0 opacity-0 pointer-events-none' : `max-w-xs opacity-100 ${isMobile ? 'pl-4' : 'pl-1.5'}`}
                `}>
                    <div className="truncate">{item.label}</div>
                </div>
            </div>
        );

        return (
            <div key={item.path} className={isMobile ? 'mb-2' : 'mb-0.5'}>
                <Link
                    to={item.path || '#'}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                    onClick={() => isMobile && closeMobileMenu()}
                    className="group block"
                >
                    {menuItemContent}
                </Link>
            </div>
        );
    };

    if (isMobile) {
        return (
            <>
                {/* Mobile overlay backdrop */}
                {isOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 md:hidden"
                        onClick={closeMobileMenu}
                    />
                )}

                {/* Mobile Menu */}
                <div
                    className={`fixed top-0 left-0 bottom-0 z-50 w-full transform bg-white overflow-y-auto transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="flex flex-col justify-between h-screen bg-white px-3 py-4">
                        {/* Mobile Header */}
                        <div className="flex items-center justify-between px-3 pb-3 border-b border-gray-100">
                            <span className="font-extrabold text-xl text-slate-800">
                                {t('menu')}
                            </span>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="lg"
                                onClick={closeMobileMenu}
                                radius="lg"
                            >
                                <IconX size={20} />
                            </ActionIcon>
                        </div>

                        <nav className="flex-1 overflow-y-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            <div className="mt-2">
                                {visibleMenuItems.map((item, idx) => renderMenuItem(item, idx))}
                            </div>
                        </nav>

                        <div className="relative flex flex-col gap-2 border-t border-gray-100/80 pt-4 pb-6 w-full">
                            <div className="flex items-center w-full px-2">
                                <div className="flex-1 min-w-0">
                                    <UserMenu inline={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Desktop LeftMenu
    return (
        <div
            className={`absolute top-0 left-0 bottom-0 bg-white flex flex-col justify-between transition-all duration-300 border-r border-gray-200/80 ${!isPinned && isHovered ? 'z-30' : ''}`}
            style={{ width: currentWidth }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex flex-col justify-between h-full bg-white px-2 py-4">
                <nav className="flex-1 overflow-y-auto pr-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <div>
                        {visibleMenuItems.map((item, idx) => renderMenuItem(item, idx))}
                    </div>
                </nav>

                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={togglePinned}
                    title={isPinned ? t('unpin_menu') : t('pin_menu')}
                >
                    <IconPin
                        size={14}
                        stroke={1.5}
                        className={isPinned ? 'text-green-600 rotate-315' : 'text-gray-400'}
                        style={{ transition: 'transform 0.2s, color 0.2s' }}
                    />
                </ActionIcon>

                <div className="flex items-center h-10 w-full px-2">
                    <div className="flex-1 min-w-0">
                        <UserMenu isCollapsed={isCollapsed} />
                    </div>
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0
                            ${isCollapsed ? 'max-w-0 opacity-0 pointer-events-none' : 'max-w-xs opacity-100 ml-2'}
                        `}>

                    </div>
                </div>
            </div>
        </div>
    );
}